import type { AppScreen, DashboardView } from "./navigation.js";

/**
 * URL is the source of truth for which screen is open (D142).
 *
 * Screen state used to live only in React state, so a refresh dropped the
 * advocate back on the overview, the browser's back button did nothing, and a
 * matter could not be bookmarked or sent to a colleague. For a workspace an
 * advocate keeps open across a working day, losing their place on reload is not
 * a small annoyance.
 */

export interface Route {
  readonly screen: AppScreen;
  readonly dashboardView: DashboardView;
  readonly matterId: string | null;
  readonly threadId: string | null;
}

/**
 * Where an advocate lands (D223).
 *
 * This used to be the dashboard. A dashboard is what you build when the product
 * is a set of features; the product is a conversation, so the first thing on
 * screen is a composer. The overview is still one click away and still
 * bookmarkable, it is just no longer the front door.
 */
const NEW_CHAT: Route = {
  screen: "workspace",
  dashboardView: "overview",
  matterId: null,
  threadId: null,
};

const OVERVIEW: Route = {
  screen: "dashboard",
  dashboardView: "overview",
  matterId: null,
  threadId: null,
};

/** Screens that are a bare path with no further state. */
const SIMPLE: Readonly<Record<string, AppScreen>> = {
  billing: "billing",
  settings: "settings",
  team: "team",
};

export const routeForPath = (pathname: string): Route => {
  const segments = pathname.split("/").filter((part) => part !== "");
  const [first, second, third, fourth] = segments;

  if (first === undefined) return NEW_CHAT;
  if (first === "overview") return OVERVIEW;

  const simple = SIMPLE[first];
  if (simple !== undefined) {
    return {
      screen: simple,
      dashboardView: "overview",
      matterId: null,
      threadId: null,
    };
  }

  if (first === "chat") {
    return {
      screen: "workspace",
      dashboardView: "overview",
      matterId: null,
      threadId:
        second === undefined || second === "new" ? null : decodeURIComponent(second),
    };
  }

  if (first === "draft") {
    return {
      screen: "workspace",
      dashboardView: "overview",
      matterId: null,
      threadId: null,
    };
  }

  if (first === "matters") {
    if (second === undefined) {
      return {
        screen: "dashboard",
        dashboardView: "matters",
        matterId: null,
        threadId: null,
      };
    }
    return {
      screen: "workspace",
      dashboardView: "matters",
      matterId: decodeURIComponent(second),
      threadId:
        third === "chat" && fourth !== undefined && fourth !== "new"
          ? decodeURIComponent(fourth)
          : null,
    };
  }

  // A stale bookmark should land somewhere usable rather than a blank screen.
  return NEW_CHAT;
};

export const pathForRoute = (route: Route): string => {
  if (route.screen === "workspace") {
    if (route.matterId === null) {
      return route.threadId === null
        ? "/chat/new"
        : `/chat/${encodeURIComponent(route.threadId)}`;
    }
    const matterPath = `/matters/${encodeURIComponent(route.matterId)}/chat`;
    return route.threadId === null
      ? `${matterPath}/new`
      : `${matterPath}/${encodeURIComponent(route.threadId)}`;
  }
  if (route.screen === "dashboard") {
    return route.dashboardView === "matters" ? "/matters" : "/overview";
  }
  return `/${route.screen}`;
};
